/**
 * Navigation guard for the pricing surface's autosave model.
 *
 * The pricing page autosaves each section's draft on every edit (there is no
 * Save button). Between an edit and its successful save these windows exist where
 * an edit is NOT yet persisted and would be lost if the operator navigated away:
 *   1. the ~800ms autosave debounce window,
 *   2. an invalid draft (e.g. a tax mid-switch from percentage to a still-empty
 *      flat amount) that the server rejects — it never persists until fixed,
 *   3. an autosave gated by an incomplete draft (e.g. a fee row without a
 *      Leistungskategorie): nothing is sent at all.
 * In all cases the section's Alpine state (the source of truth for the
 * in-progress edit) is discarded on any full navigation, silently losing work.
 *
 * This guard mirrors upload-guard.ts: a browser `beforeunload` prompt for hard
 * navigations (reload, tab close, external link, back button) plus an
 * `htmx:beforeOnLoad` hook for HX-Refresh/HX-Redirect responses. For in-app
 * navigation it intercepts the nav-link CLICK in the capture phase (not the
 * htmx request), so cancelling also blocks the link's own Alpine @click that
 * flips the active-section store — otherwise the sidebar highlight and
 * app-shell header would desync from the still-showing pricing content.
 *
 * Dirty tracking is per section-form id, self-healing: a form is marked dirty
 * the moment an edit dispatches `pricing-autosave`, cleared when that form's
 * autosave POST returns the `pricing-config-autosaved` success trigger, and
 * kept dirty on an invalid/failed save. Ids whose form no longer exists in the
 * DOM (e.g. after a #pricing-content self-refresh replaced the forms with
 * freshly-saved ones) are pruned before every dirty check, so the set can never
 * go stale and falsely block navigation.
 *
 * Idempotent: safe to import/call more than once.
 */
export function registerPricingAutosaveGuard() {
  if ((window as any).__studioPricingAutosaveGuardRegistered) return;
  (window as any).__studioPricingAutosaveGuardRegistered = true;

  // Section forms carry hx-trigger="pricing-autosave …"; this selector both
  // identifies our forms and finds the enclosing form from an event target.
  const FORM_SELECTOR = "form[hx-trigger*='pricing-autosave']";
  const dirty = new Set<string>();

  const formFrom = (target: EventTarget | null): HTMLFormElement | null => {
    const node = target as HTMLElement | null;
    return (node?.closest?.(FORM_SELECTOR) as HTMLFormElement | null) ?? null;
  };

  const isDirty = (): boolean => {
    // Prune ids whose form left the DOM (navigated away, or replaced by a
    // self-refresh that already carries the saved state) so we never prompt
    // for a section that no longer exists.
    for (const id of dirty) {
      if (!document.getElementById(id)) dirty.delete(id);
    }
    return dirty.size > 0;
  };

  const message = (): string =>
    (window as any).STUDIO_CONFIG?.pricing?.confirm_navigate_unsaved ||
    "Es gibt ungespeicherte Preisänderungen. Wenn du die Seite verlässt, gehen sie verloren. Trotzdem fortfahren?";

  // Edit made → a save is (or will shortly be) pending. Mark dirty NOW, before
  // the debounced request fires, so the debounce window itself is protected.
  // The event bubbles from the section form (see pricingAutosaveRootAttrs).
  document.addEventListener("pricing-autosave", (e: Event) => {
    const form = formFrom(e.target);
    if (form?.id) dirty.add(form.id);
  });

  // Autosave gated by an incomplete draft (pricingAutosaveRootAttrs'
  // blockedExpr — e.g. a fee row without a Leistungskategorie): nothing was
  // sent, so the section is unsaved. Mark it dirty so navigation warns.
  document.addEventListener("pricing-autosave-blocked", (e: Event) => {
    const form = formFrom(e.target);
    if (form?.id) dirty.add(form.id);
  });

  // Save resolved. Only the response's HX-Trigger header distinguishes a
  // successful save (pricing-config-autosaved) from a rejected invalid draft
  // (pricing-config-autosave-invalid) — both are HTTP 204 — so read it here,
  // where htmx hands us the exact posting form via detail.elt.
  document.addEventListener("htmx:afterRequest", (e: any) => {
    const elt = e.detail?.elt as HTMLElement | undefined;
    if (!elt?.matches?.(FORM_SELECTOR)) return;
    const id = (elt as HTMLFormElement).id;
    if (!id) return;
    const trigger = e.detail?.xhr?.getResponseHeader?.("HX-Trigger") || "";
    if (e.detail?.successful && trigger.includes("pricing-config-autosaved")) {
      dirty.delete(id); // persisted — this section is clean
    } else {
      dirty.add(id); // invalid draft or network failure — still unsaved
    }
  });

  // A #pricing-content self-refresh (cancellation-preset adopt/clear →
  // pricing-config-saved, publish, restore) re-renders every section form from the authoritative saved draft,
  // reusing the same ids. Those fresh forms are clean, so drop all tracked ids
  // — otherwise a stale id would keep the (now-saved) surface falsely dirty.
  ["pricing-config-saved", "pricing-published", "pricing-restored"].forEach((evt) => {
    document.addEventListener(evt, () => dirty.clear());
  });

  // In-app navigation. A nav link both fires an htmx request AND runs its own
  // Alpine @click that flips the active-section store (which the sidebar
  // highlight and app-shell header bind to). Cancelling only the htmx request
  // (htmx:beforeRequest) would leave that store already mutated — the header +
  // sidebar would jump to the new section while the pricing content stayed put.
  // So intercept the CLICK in the capture phase, BEFORE the link's @click and
  // htmx run: on cancel, stopImmediatePropagation() blocks BOTH, so nothing
  // changes; on confirm, the event flows normally.
  //
  // Section switches carry hx-target="#property-content" and full boosted navs
  // target <body> — both CONTAIN the pricing section forms. We prompt only when
  // the link's swap container holds a still-dirty form. Plain (non-htmx) <a>
  // links do a full navigation already covered by beforeunload, so we leave
  // them alone here to avoid a double prompt; likewise links inside
  // #pricing-content (pricing's own controls).
  document.addEventListener(
    "click",
    (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (!isDirty()) return;
      const link = (e.target as HTMLElement)?.closest?.("a") as HTMLAnchorElement | null;
      if (!link || link.closest("#pricing-content")) return;

      let container: HTMLElement | null = null;
      const targetSel = link.getAttribute("hx-target");
      if (targetSel) {
        container = document.querySelector<HTMLElement>(targetSel);
      } else if (
        link.getAttribute("hx-boost") !== "false" &&
        (link.hasAttribute("hx-get") || link.hasAttribute("hx-post"))
      ) {
        container = document.body;
      }
      if (!container) return;

      const clobbersUnsaved = [...dirty].some((id) => {
        const form = document.getElementById(id);
        return form != null && container!.contains(form);
      });
      if (!clobbersUnsaved) return;

      // NATIVE confirm on purpose (allowlisted in scripts/lint_window_confirm.sh):
      // this capture-phase click guard must decide synchronously, before htmx's
      // boost handler sees the click. (It COULD become async like the wiki save
      // gate — always block, then re-click with a bypass flag on confirm — but
      // that is a behaviour change left for a dedicated pass.)
      if (!window.confirm(message())) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true,
  );

  // Hard reload / redirect responses (same hook upload-guard uses).
  document.addEventListener("htmx:beforeOnLoad", (e: any) => {
    const xhr = e.detail?.xhr;
    if (!xhr) return;
    const willReload =
      xhr.getResponseHeader("HX-Refresh") === "true" || !!xhr.getResponseHeader("HX-Redirect");
    if (!willReload || !isDirty()) return;
    // NATIVE confirm on purpose (see upload-guard.ts): htmx:beforeOnLoad must be
    // answered synchronously within this dispatch.
    if (!window.confirm(message())) e.preventDefault();
  });

  // Reload, tab close, external link, back button.
  window.addEventListener("beforeunload", (e: BeforeUnloadEvent) => {
    if (!isDirty()) return;
    e.preventDefault();
    e.returnValue = "";
  });
}
